import { useState } from 'react';
import { CheckIcon, CopyIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { copyTextToClipboard } from '@/lib/clipboard';

type IdBadgeProps = {
  id: string;
  label?: string;
  className?: string;
};

/** Shortens long ids the same way as the prototype UIKit.idBadge helper. */
function shortId(id: string): string {
  return id.length <= 22 ? id : `${id.slice(0, 10)}…${id.slice(-6)}`;
}

/**
 * Copy-to-clipboard chip for a resource itemId (prototype UIKit.idBadge).
 * Ids are user-facing here — developers paste them into listIds / segmentIds / APIs.
 */
export function IdBadge({ id, label = 'ID', className }: IdBadgeProps) {
  const [copied, setCopied] = useState(false);

  if (!id) return null;

  const onCopy = async () => {
    const ok = await copyTextToClipboard(id);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };

  return (
    <button
      type="button"
      onClick={() => void onCopy()}
      title={`${label}: ${id} — click to copy`}
      aria-label={`Copy ${label}, ${id}`}
      className={cn(
        'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md border border-[#DEE0E3] bg-[#F4F5F6] px-2 py-0.5 leading-5 transition-colors hover:border-[#CECECE] hover:bg-[#E7E8EA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
        copied && 'border-[#1F8A46] bg-[#E7F4EC]',
        className
      )}
    >
      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#666666]">
        {label}
      </span>
      <code className="min-w-0 truncate font-mono text-xs text-[#27272C]">{shortId(id)}</code>
      {copied ? (
        <CheckIcon className="size-3.5 shrink-0 text-[#1F8A46]" aria-hidden />
      ) : (
        <CopyIcon className="size-3.5 shrink-0 text-[#666666]" aria-hidden />
      )}
    </button>
  );
}
